/**
 * WorkspaceModal - Initial workspace selection modal
 *
 * Shown when:
 * - User logs in with multiple workspaces
 * - No workspace is currently selected
 *
 * Features:
 * - List of available workspaces
 * - Search functionality
 * - "Create new" option
 * - Cannot be dismissed without selection (optional)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Building2, Search, Check, FolderPlus, X, ArrowRight, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useI18n } from '../i18n';

const WorkspaceModal = ({
  show,
  onClose,
  allowClose = true,
  redirectTo = null
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const {
    workspaces,
    loading,
    switchWorkspace,
    fetchWorkspaces,
    setShowSelector,
  } = useWorkspace();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  // Fetch workspaces on mount
  useEffect(() => {
    if (show) {
      fetchWorkspaces();
    }
  }, [show, fetchWorkspaces]);

  // Filter workspaces
  const filteredWorkspaces = useMemo(() => {
    if (!search.trim()) return workspaces;
    const term = search.toLowerCase();
    return workspaces.filter(
      (ws) =>
        ws.name?.toLowerCase().includes(term) ||
        ws.company_name?.toLowerCase().includes(term) ||
        ws.city?.toLowerCase().includes(term)
    );
  }, [search, workspaces]);

  // Handle selection confirmation
  const handleConfirm = async () => {
    if (!selectedId) return;

    setSwitching(true);
    setError(null);

    try {
      const success = await switchWorkspace(selectedId);

      if (success) {
        setShowSelector(false);
        if (onClose) onClose();
        if (redirectTo) {
          navigate(redirectTo);
        }
        // Reload to apply new workspace context
        window.location.reload();
      } else {
        setError(t('workspace.switchError', 'Erreur lors du changement de workspace'));
      }
    } catch (err) {
      setError(err.message || t('workspace.switchError', 'Erreur lors du changement de workspace'));
    } finally {
      setSwitching(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!allowClose) return;
    if (onClose) onClose();
    setShowSelector(false);
  };

  // Navigate to create workspace
  const handleCreateNew = () => {
    setShowSelector(false);
    navigate('/workspaces?action=create');
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={allowClose ? handleClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-black border border-brand-green/30
                      rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-green/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {t('workspace.selectTitle', 'Workspace selection')}
              </h2>
              <p className="text-sm text-gray-400">
                {t('workspace.selectDescription', 'Choisissez un workspace pour continuer')}
              </p>
            </div>
          </div>
          {allowClose && (
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg
                         hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-brand-green/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('workspace.searchPlaceholder', 'Rechercher un workspace...')}
              className="w-full pl-10 pr-4 py-3 bg-brand-black/50 border border-brand-green/20
                         rounded-lg text-white placeholder-gray-400
                         focus:outline-none focus:border-brand-green/50"
            />
          </div>
        </div>

        {/* Workspace List */}
        <div className="max-h-64 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-brand-green border-t-transparent
                              rounded-full animate-spin" />
            </div>
          ) : filteredWorkspaces.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {workspaces.length === 0
                  ? t('workspace.noWorkspacesYet', 'No workspace created')
                  : t('workspace.noResults', 'No workspace found')}
              </p>
              {workspaces.length === 0 && (
                <button
                  onClick={handleCreateNew}
                  className="mt-4 text-brand-green hover:text-brand-green/80 text-sm
                             flex items-center gap-2 mx-auto"
                >
                  <FolderPlus className="w-4 h-4" />
                  {t('workspace.createFirst', 'Create your first workspace')}
                </button>
              )}
            </div>
          ) : (
            filteredWorkspaces.map((ws) => (
              <button
                key={ws.id}
                onClick={() => setSelectedId(ws.id)}
                className={`w-full px-4 py-3 text-left rounded-lg transition-all mb-1
                           flex items-center gap-3 ${
                             selectedId === ws.id
                               ? 'bg-brand-green/20 border border-brand-green/50'
                               : 'hover:bg-brand-green/10 border border-transparent'
                           }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                                ${selectedId === ws.id ? 'bg-brand-green/30' : 'bg-brand-black/50'}`}>
                  <Building2 className={`w-5 h-5 ${
                    selectedId === ws.id ? 'text-brand-green' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium truncate">{ws.name}</span>
                    {selectedId === ws.id && (
                      <Check className="w-4 h-4 text-brand-green flex-shrink-0" />
                    )}
                  </div>
                  {ws.company_name && (
                    <span className="text-xs text-gray-400 truncate block">
                      {ws.company_name}
                      {ws.city && ` - ${ws.city}`}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {ws.scans_count || 0} {(ws.scans_count || 0) === 1 ? t('workspace.scan', 'scan') : t('workspace.scans', 'scans')}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-brand-green/20
                        bg-brand-black/30">
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white
                       transition-colors"
          >
            <FolderPlus className="w-4 h-4" />
            {t('workspace.createNew', 'Create a workspace')}
          </button>

          <button
            onClick={handleConfirm}
            disabled={!selectedId || switching}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm
                       transition-all ${
                         selectedId && !switching
                           ? 'bg-brand-green text-black hover:bg-brand-green/90'
                           : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                       }`}
          >
            {switching ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black/80
                                rounded-full animate-spin" />
                {t('common.loading', 'Chargement...')}
              </>
            ) : (
              <>
                {t('workspace.continue', 'Continuer')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceModal;
